import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

/**
 * Étape 3 — dispatch armé : consomme la route lecture-seule de l'Étape 1
 * (`GET /api/cta/vehicles/crew-status`) pour afficher, avant tout engagement,
 * quels engins sont armables et lesquels sont « disponible matériel ».
 */

export type CrewMissingReason = 'absente' | 'indispo';

export interface CrewMissing {
  competenceId: number;
  competenceName: string;
  reason: CrewMissingReason;
}

export interface VehicleCrewStatus {
  vehicleId: number;
  nom: string | null;
  typeCode: string | null;
  typeNom: string | null;
  buildingId: number | null;
  vehicleStatusId: number | null;
  engaged: boolean;
  armable: boolean | null;
  noCompetenceRequired: boolean;
  crew: number[];
  missing: CrewMissing[];
}

export interface CrewStatusStaff {
  nom: string;
  prenom: string;
  grade: string | null;
}

interface CrewStatusResult {
  vehicles: VehicleCrewStatus[];
  staffById: Record<number, CrewStatusStaff>;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export function useVehicleCrewStatus(pollMs = 20000): CrewStatusResult {
  const [vehicles, setVehicles] = useState<VehicleCrewStatus[]>([]);
  const [staffById, setStaffById] = useState<Record<number, CrewStatusStaff>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const aliveRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/cta/vehicles/crew-status`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Erreur crew-status');
      if (!aliveRef.current) return;
      setVehicles(Array.isArray(data.vehicles) ? data.vehicles : []);
      setStaffById(data.staffById && typeof data.staffById === 'object' ? data.staffById : {});
      setError(null);
    } catch (err: any) {
      if (!aliveRef.current) return;
      setError(err?.message || 'Erreur serveur');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    void load();
    if (pollMs <= 0) return () => { aliveRef.current = false; };
    const id = setInterval(() => void load(), pollMs);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [load, pollMs]);

  return { vehicles, staffById, loading, error, refresh: () => void load() };
}
